import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Row, Col, Modal, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";
import "bootstrap/dist/css/bootstrap.min.css";
import DynamicBreadcrumb from "../header/breadcrumb";
import employeeApi from "../../services/employeeapi";
import { showConfirm } from "react-confirm-prompt";
import Loader from "../loader/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import EmployeeViewModal from "./EmployeeViewModal";
import EmployeeCreateForm from "./EmployeeCreateForm";
import branchApi from "../../services/branchapi";
library.add(fas);

const AllEmployees = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [totalEmployee, setTotalEmployee] = useState(0);
    const [refreshTable, setRefreshTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });
    const [openInactivateModal, setOpenInactivateModal] = useState(false); // For Inactivation Reason modal
    const [closeNotes, setCloseNotes] = useState("");
    const [errors, setErrors] = useState({});
    const [employeeId, setEmployeeId] = useState(null);
    const [invModal, setInvModal] = useState(false); // For View modal
    const [employeeStatus, setEmployeeStatus] = useState(null);
    const [loaderVisible, setLoaderVisible] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false); // For Create Employee form
    const [editingEmployeeId, setEditingEmployeeId] = useState(null); // Store the ID for editing

    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);


    const pathArray = [{ name: "Employee List", url: null }];

    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const data = await employeeApi.getAllEmployees({
                limit: pageSize,
                page,
                sortModel,
                filterModel,
            });

            const formattedRows = data.data.result.map((emp, index) => ({
                id: emp.id,
                sno: index + 1,
                name: emp.name,
                email: emp.email,
                mobile: emp.mobile,
                branch: emp.employeeBranch?.branchName ?? "N/A",
                department: emp.employeeDepartment?.name ?? "N/A",
                designation: emp.employeeDesignation?.name ?? "N/A",
                created_at: moment(emp.updatedAt).format("YYYY/MM/DD kk:mm:ss"),
                status: emp.status,
            }));
            setRows(formattedRows);
            setTotalEmployee(data.data.totalRecords || 0);
        } catch (error) {
            console.error("Failed to fetch Employees:", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const sortModel = [{ field: "name", sort: "asc" }];
                const filterParams = { limit: 100, page: 0, status: 1, sortModel };

                const [departmentData, designationData, branchData, employeeData] = await Promise.all([
                    employeeApi.getDepartments(filterParams),
                    employeeApi.getDesignations(filterParams),
                    branchApi.getAllBranches({
                        limit: 100,
                        page: 0,
                        status: 1,
                        sortModel: [{ field: "branchName", sort: "asc" }],
                    }),
                    employeeApi.getAllEmployees(filterParams),
                ]);

                setDepartments(departmentData.data.result || []);
                setDesignations(designationData.data.result || []);
                setBranches(branchData.data.result || []);
                setEmployees(employeeData.data.result || []);
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };

        fetchDropdownData();
    }, []);


    const handleStatusClick = (employeeId, status) => {
        const confirmText =
            status === 1
                ? "Are you sure you want to inactivate this employee?"
                : "Are you sure you want to activate this employee?";
        const description =
            status === 1
                ? "This will inactivate the employee"
                : "This will activate the employee";

        showConfirm(confirmText, {
            description,
            type: "info",
        }).then((answer) => {
            if (answer) {
                setEmployeeId(employeeId);
                setEmployeeStatus(status);
                if (status !== 1) {
                    submitUpdateDetails(employeeId, status);
                } else {
                    setOpenInactivateModal(true); // Show Inactivation Modal
                }
            }
        });
    };

    const columns = [
        { field: "sno", headerName: "S NO", sortable: false },
        { field: "created_at", headerName: "Updated ON", flex: 1 },
        { field: "name", headerName: "Employee Name", flex: 1 },
        { field: "mobile", headerName: "Mobile", flex: 1 },
        { field: "email", headerName: "Email", flex: 1 },
        { field: "branch", headerName: "Branch", flex: 1 },
        { field: "department", headerName: "Department", flex: 1 },
        { field: "designation", headerName: "Designation", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Row>
                    <Col className="ms-2 p-2 me-2 d-grid gap-2">
                        <button
                            onClick={() =>
                                handleStatusClick(params.id, parseInt(params.value))
                            }
                            className={`btn ${params.value === 1 ? "btn-danger" : "btn-secondary"
                                } custom-status-button`}
                            style={{ outline: "none", boxShadow: "none" }}
                        >
                            {params.value === 1 ? "Active" : "Inactive"}
                        </button>
                    </Col>
                </Row>
            ),
        },
        {
            headerName: "Action",
            flex: 1,
            renderCell: (params) => (
                <Row>
                    <Col className="ms-2 p-2 me-2 d-grid gap-2">
                        <Button
                            size="sm"
                            onClick={() => openEditEmployeeForm(params.id)} // Open the form for editing
                            className="p-2"
                        >
                            <FontAwesomeIcon
                                icon="fa-solid fa-pencil-alt"
                                size="sm"
                                style={{ color: "black" }}
                            />{" "}
                            Edit
                        </Button>
                    </Col>
                    <Col className="ms-2 p-2 me-2 d-grid gap-2">
                        <Button
                            size="sm"
                            onClick={() => openViewModel(params.id)}
                            className="p-2"
                        >
                            <FontAwesomeIcon
                                icon="fa-solid fa-eye"
                                size="sm"
                                style={{ color: "black" }}
                            />{" "}
                            View
                        </Button>
                    </Col>
                </Row>
            ),
        },
    ];

    const openViewModel = (id) => {
        setEmployeeId(id);
        setInvModal(true); // Open View modal
    };

    const hideViewModal = () => {
        setEmployeeId(null);
        setInvModal(false); // Close View modal
    };

    const validateCancelForm = () => {
        const newErrors = {};
        if (!closeNotes) newErrors.closeNotes = "Notes is required";
        return newErrors;
    };

    const handleInactivate = async (event) => {
        event.preventDefault();
        const formErrors = validateCancelForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        } else {
            setErrors({});
            try {
                submitUpdateDetails(employeeId, employeeStatus, closeNotes);
            } catch (error) {
                setErrors({ common: "Update Failed" });
            }
        }
    };

    const submitUpdateDetails = async (employeeId, status, notes = null) => {
        try {
            let sendStatus = status === 1 ? 2 : 1;
            let params = {
                action: "status",
                status: sendStatus,
                id: employeeId,
                reason: notes,
            };

            const resp = await employeeApi.updateEmployeeStatus(params);
            if (resp) {
                showConfirm("Success", {
                    description: resp.message || "Employee status updated",
                    type: "success",
                    hideCancel: true,
                }).then(() => {
                    setRefreshTable(!refreshTable);
                    handleClose();
                });
            } else {
                showConfirm("Oops!", {
                    description: "Process failed. Try again later.",
                    type: "warning",
                    hideCancel: true,
                });
            }
            handleClose();
        } catch (error) {
            showConfirm("Oops!", {
                description: "Something went wrong!",
                type: "warning",
                hideCancel: true,
            });
            handleClose();
        }
    };

    const handleClose = () => {
        setOpenInactivateModal(false); // Close inactivation modal
        setCloseNotes("");
    };

    const openCreateEmployeeForm = () => {
        setEditingEmployeeId(null); // Reset ID for new employee
        setShowCreateModal(true); // Open the Create Employee modal
        // console.log(showCreateModal, "showCreateModal");
    };

    const openEditEmployeeForm = (employeeId) => {
        setEditingEmployeeId(employeeId); // Set ID for the employee being edited
        setShowCreateModal(true); // Open the Edit Employee modal
    };

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>ALL Employees</h5>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={openCreateEmployeeForm}>
                        Create Employee
                    </Button>
                </div>
            </div>
            {loaderVisible && <Loader />}
            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    rowsPerPageOptions={[5, 10, 20]}
                    paginationMode="server"
                    rowCount={totalEmployee}
                    loading={loading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode="server"
                    filterMode="server"
                    onSortModelChange={setSortModel}
                    onFilterModelChange={setFilterModel}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    onRowSelectionModelChange={(row) => setRowSelectionModel(row)}
                    rowSelectionModel={rowSelectionModel}
                    sx={{
                        [`& .${gridClasses.cell}`]: { py: 0.5 },
                    }}
                />
            </Box>

            {/* Employee View Modal */}
            {employeeId && (
                <EmployeeViewModal
                    employeeId={employeeId}
                    invModal={invModal}
                    change={hideViewModal}
                />
            )}

            {/* Inactivation Reason Modal */}
            <Modal show={openInactivateModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Enter Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Inactivation Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={closeNotes}
                                onChange={(e) => setCloseNotes(e.target.value)}
                                isInvalid={!!errors.closeNotes}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.closeNotes}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleInactivate}>
                        Submit
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {showCreateModal && (
                <EmployeeCreateForm
                    show={showCreateModal}  // Pass showCreateModal state
                    handleClose={() => { setShowCreateModal(false); setEditingEmployeeId(null); }}
                    handleCreateEmployee={() => setRefreshTable(!refreshTable)}  // Pass refresh callback
                    editingEmployeeId={editingEmployeeId}  // Pass the ID for editing
                    departments={departments}
                    designations={designations}
                    branches={branches}
                    employees={employees}
                />
            )}

        </Container>
    );
};

export default AllEmployees;
